import type { WebsocketOutgoingMessage } from '@/types/WebsocketApi'
import { fetchAuthSession } from 'aws-amplify/auth'

export class WebSocketWrapper {
  url: string
  timeout: number = 10000
  reconnectDelay: number = 3000
  onReceive?: (event: MessageEvent) => void
  onConnect?: (event: Event) => void
  onError?: (event: Event) => void
  onClose?: (event: Event) => void

  private ws: WebSocket | null = null
  private timeoutHandle?: number | undefined
  private shouldReconnectAfterClose: boolean = true
  private manualReconnectionInProgress: boolean = false
  private beforeReconnectCallback?: () => void

  constructor(params: {
    url: string
    timeout?: number
    reconnectDelay?: number
    onReceive?: (event: MessageEvent) => void
    onConnect?: (event: Event) => void
    onError?: (event: Event) => void
    onClose?: (event: Event) => void
  }) {
    this.url = params.url
    this.shouldReconnectAfterClose = true

    //add external handlers if provided
    if (params.onReceive) {
      this.onReceive = params.onReceive
    }
    if (params.onConnect) {
      this.onConnect = params.onConnect
    }
    if (params.onError) {
      this.onError = params.onError
    }
    if (params.onClose) {
      this.onClose = params.onClose
    }
    if (params.timeout) {
      this.timeout = params.timeout
    }
    if (params.reconnectDelay) {
      this.reconnectDelay = params.reconnectDelay
    }
  }

  async init() {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString()
    this.ws = new WebSocket(this.url + `?token=${token}`)

    //add internal handlers
    this.ws.addEventListener('message', this.receiveHandler)
    this.ws.addEventListener('open', this.ConnectHandler)
    this.ws.addEventListener('close', this.closeHandler)
    this.ws.addEventListener('error', this.ErrorHandler)

    //add external handlers if provided
    if (this.onReceive) {
      this.ws.addEventListener('message', this.onReceive)
    }
    if (this.onConnect) {
      this.ws.addEventListener('open', this.onConnect)
    }
    if (this.onError) {
      this.ws.addEventListener('error', this.onError)
    }
    if (this.onClose) {
      this.ws.addEventListener('close', this.onClose)
    }
  }

  manualReconnect(beforeReconnectCallback?: () => void) {
    this.beforeReconnectCallback = beforeReconnectCallback
    this.manualReconnectionInProgress = true
    this.ws?.close()
  }

  private async continueReconnectAfterClose() {
    this.beforeReconnectCallback?.()
    this.beforeReconnectCallback = undefined
    this.shouldReconnectAfterClose = true

    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString()
    if (!token) {
      console.error('not logged in, retrying')
      window.clearTimeout(this.timeoutHandle)
      window.setTimeout(() => {
        this.continueReconnectAfterClose()
      }, this.reconnectDelay)

      return
    }

    this.ws = new WebSocket(this.url + `?token=${token}`)

    //add internal handlers
    this.ws.addEventListener('message', this.receiveHandler)
    this.ws.addEventListener('open', this.ConnectHandler)
    this.ws.addEventListener('close', this.closeHandler)
    this.ws.addEventListener('error', this.ErrorHandler)

    if (this.onReceive) {
      this.ws.addEventListener('message', this.onReceive)
    }
    if (this.onConnect) {
      this.ws.addEventListener('open', this.onConnect)
    }
    if (this.onError) {
      this.ws.addEventListener('error', this.onError)
    }
    if (this.onClose) {
      this.ws.addEventListener('close', this.onClose)
    }
  }

  close() {
    window.clearTimeout(this.timeoutHandle)
    this.shouldReconnectAfterClose = false
    this.ws?.close()
  }

  send(data: WebsocketOutgoingMessage) {
    this.ws?.send(JSON.stringify(data))
  }

  getState(): number | undefined {
    return this.ws?.readyState
  }

  private rearmTimeout() {
    window.clearTimeout(this.timeoutHandle)
    if (this.timeout) {
      this.timeoutHandle = window.setTimeout(() => {
        console.error('WebSocket timeouted. attempting to reconnect...')
        this.ws?.close()
      }, this.timeout)
    }
  }

  private ConnectHandler = (event: Event) => {
    console.log('WebSocket connected, setting timeout', event)
    this.rearmTimeout()
  }

  private ErrorHandler = (event: Event) => {
    console.error('WebSocket error. attempting to reconnect...', event)
    window.clearTimeout(this.timeoutHandle)
    this.close()
    window.setTimeout(() => {
      this.continueReconnectAfterClose()
    }, this.reconnectDelay)
  }

  //TODO - what if connection is closed by server? should we attempt to reconnect? yes
  private closeHandler = (event: Event) => {
    console.log('WebSocket closed,', event)
    window.clearTimeout(this.timeoutHandle)
    if (this.manualReconnectionInProgress) {
      this.continueReconnectAfterClose()
    } else if (this.shouldReconnectAfterClose) {
      window.setTimeout(() => {
        this.continueReconnectAfterClose()
      }, this.reconnectDelay)
    }
  }

  private receiveHandler = () => {
    // console.log('WebSocket message received', event)
    this.rearmTimeout()
  }
}
